import React, { ForwardedRef, forwardRef } from "react";

import { useFormAtom } from "@impero-com/common/utils/form/formAtom/hooks";
import { FormAtom } from "@impero-com/common/utils/form/formAtom/types";

import { MarkdownInput } from "components/form/fields/markdownInput";
import {
    MarkdownEditorCustomButtonSpec,
    MarkdownEditorMethods,
    MarkdownSelectionRange,
    SourceToggleMode,
} from "components/markdownEditor/types";

export interface FAMarkdownInputProps {
    atom: FormAtom<string>;
    label?: React.ReactNode;
    disabled?: boolean;
    // The text rendered when the atom is holding an empty string
    placeholder?: string;
    // normal (default): A regular input, with borders, gray-background when disabled
    // borderless: An input without borders, unless focused
    styleVariant?: "normal" | "borderless";
    // Gives focus to this component when mounted
    autoFocus?: boolean;
    sourceToggleMode?: SourceToggleMode;
    customButtonSpecs?: Array<MarkdownEditorCustomButtonSpec>;
    headerAction?: React.ReactNode;
    onSelect?: (selection?: MarkdownSelectionRange) => void;
    // When true, keeps the editor open even when it loses focus
    keepEditorOpen?: boolean;
}

/**
 * Basic form component based on ant design's TextArea
 *
 * Renders markdown content when unfocused and without error
 *
 * This component leverages the following constraints from the validator:
 *  - `min(n)` is mapped to `minLength={n}`
 *  - `max(n)` is mapped to `maxLength={n}`
 *  - `required` is mapped to `required`
 */
export const FAMarkdownInput = forwardRef(
    (
        {
            atom,
            label,
            disabled = false,
            placeholder,
            styleVariant = "normal",
            autoFocus = false,
            sourceToggleMode,
            customButtonSpecs,
            headerAction,
            onSelect,
            keepEditorOpen,
        }: FAMarkdownInputProps,
        forwardedRef: ForwardedRef<MarkdownEditorMethods>
    ) => {
        const { value, error, setCacheValue, dispatchValue } =
            useFormAtom(atom);

        const handleBlur = () => {
            void dispatchValue(value);
        };

        return (
            <MarkdownInput
                ref={forwardedRef}
                label={label}
                placeholder={placeholder}
                value={value}
                error={error?.value}
                onChange={setCacheValue}
                onBlur={handleBlur}
                disabled={disabled}
                styleVariant={styleVariant}
                autoFocus={autoFocus}
                sourceToggleMode={sourceToggleMode}
                customButtonSpecs={customButtonSpecs}
                headerAction={headerAction}
                onSelect={onSelect}
                keepEditorOpen={keepEditorOpen}
            />
        );
    }
);
